import React from "react";
import get from "lodash/get";
import Radio from "../../core/radio";
import { BooleanOption } from "../../../enums/BooleanOption";
import FormTextField from "../textField";
import FormGroup from "../formGroup";
import FormLabel from "../formLabel";
import { useFormikContext } from "formik";
import { FormUnlimitedFieldConfig } from "./type";
import { useCopies } from "../../../hooks/useCopies";
import classNames from "classnames";
import Validation from "../../Validation";
import { useFormContextData } from "shared/form/context";
import { getValueForReadOnlyFieldForStaticForm } from "shared/form/util";
import FieldRenderType from "shared/form/renderer/enums/FieldRenderType";
import { UNLIMITED_QTY } from "shared/utils/constants";

function FormUnlimitedField(props: FormUnlimitedFieldConfig) {
  const formContextData = useFormContextData();
  const copies = useCopies();
  const { setFieldValue, values } = useFormikContext<any>();
  const [hasLimit, setHasLimit] = React.useState<BooleanOption>(() => {
    return get(values, props.name)?.toString() === UNLIMITED_QTY.toString() ? BooleanOption.NO : BooleanOption.YES;
  });
  const onLimitChange = React.useCallback(
    e => {
      const selectedVal = e.target.value;
      setHasLimit(e.target.value);
      if (selectedVal === BooleanOption.NO) {
        setFieldValue(props.name, UNLIMITED_QTY.toString());
      }
      if (selectedVal === BooleanOption.YES) {
        setFieldValue(props.name, "");
      }
    },
    [setFieldValue, props.name]
  );

  const validationFn = React.useCallback(
    fieldValue => {
      return Validation.integerValidation({
        copies,
        required: Boolean(props.required),
        min: props.min ?? 0,
        max: props.max
      })
        .validate(fieldValue)
        .then(() => undefined)
        .catch(({ errors }: { errors: Array<any> }) => (errors ? errors[0] : ""));
    },
    [copies, props.required, props.min, props.max]
  );

  const getReadOnlyValue = React.useCallback(() => {
    if (hasLimit === BooleanOption.NO) {
      return copies.get("unlimited");
    }
    return values[props.name];
  }, [copies, hasLimit, props.name, values]);

  return (
    <FormGroup
      className={classNames("unlimitedFormField", props.className)}
      fullWidth={props.fullWidth}
      indented={props.indented}>
      <FormLabel
        label={props.label}
        required={props.required}
        additionalLabel={props.additionalLabel}
        infoLabel={props.infoLabel}
      />
      {formContextData.isReadOnly ? (
        getValueForReadOnlyFieldForStaticForm({
          renderType: FieldRenderType.SINGLE_LINE_TEXTFIELD,
          value: getReadOnlyValue()
        })
      ) : (
        <div className="unlimitedFormField--options">
          <div className="unlimitedFormField--options_specifyLimit">
            <Radio
              disabled={props.disabled}
              label={""}
              value={BooleanOption.YES}
              checked={hasLimit === BooleanOption.YES}
              name={`${props.name}_unlimited`}
              onChange={onLimitChange}
            />
            <FormTextField
              key={hasLimit}
              name={hasLimit === BooleanOption.YES ? props.name : `${props.name}_unlimited`}
              label={""}
              placeholder={copies.get("limit")}
              required={props.required}
              validate={hasLimit === BooleanOption.YES ? validationFn : undefined}
              disabled={hasLimit === BooleanOption.NO || props.disabled}
            />
          </div>
          <div className="unlimitedFormField--options_unlimited">
            <Radio
              label={copies.get("unlimited")}
              value={BooleanOption.NO}
              checked={hasLimit === BooleanOption.NO}
              name={`${props.name}_unlimited`}
              onChange={onLimitChange}
              disabled={props.disabled}
            />
          </div>
        </div>
      )}
    </FormGroup>
  );
}

export default FormUnlimitedField;
